<template>
  <div class="reception-page letter-spacing">
    <div class="page-head">
      <h3 class="mb-1">Nouvelle réception</h3>
      <p class="text-muted mb-0">Choisissez le type de réception à créer, puis validez pour ouvrir la note.</p>
    </div>

    <div class="row g-3">
      <!-- CLIENT -->
      <div class="col-12 col-xl-6">
        <section class="rc-card h-100">
          <header class="rc-head">
            <span class="rc-icon"><i class="ph ph-storefront"></i></span>
            <div>
              <h4>Réception client</h4>
              <small>Par store, avec recherche via un code de suivi</small>
            </div>
            <span class="rc-badge">CLIENT</span>
          </header>

          <div class="rc-body">
            <label class="rc-label">Retrouver le store via un code de suivi</label>
            <form class="rc-inline" @submit.prevent="getCstmr">
              <div class="rc-input-icon">
                <i class="ph ph-barcode"></i>
                <input v-model="codeColis" type="text" class="form-control" placeholder="Scannez ou saisissez le code de suivi" autocomplete="off" />
              </div>
              <button type="submit" class="btn btn-primary rc-btn" :disabled="isLoader || !codeColis.trim()">
                <span v-if="isLoader" class="spinner-border spinner-border-sm"></span>
                <i v-else class="flaticon-search"></i>
              </button>
            </form>

            <div class="rc-divider"><span>ou choisir manuellement</span></div>

            <div class="row g-3">
              <div class="col-12 col-md-6">
                <label class="rc-label">Store</label>
                <v-select :options="clientsList" class="custom-select-height" v-model="cstmrColi" label="customer_name" placeholder="Rechercher un store" @search="onSearchClients">
                  <template v-slot:no-options>Taper un mot pour chercher un store</template>
                  <template v-slot:option="option">
                    <span>{{ option.customer_name }} - {{ option.nb_colis }} (Colis)</span>
                  </template>
                </v-select>
              </div>
              <div class="col-12 col-md-6">
                <label class="rc-label">Agence</label>
                <v-select class="custom-select-height" label="name" :options="agencyList" placeholder="Choisir une agence" v-model="agency"></v-select>
              </div>
            </div>

            <div v-if="cstmrColi && cstmrColi.customer_name" class="rc-selected">
              <i class="ph ph-check-circle"></i>
              <span>Store sélectionné : <strong>{{ cstmrColi.customer_name }}</strong></span>
            </div>
          </div>

          <footer class="rc-foot" v-if="auth.can('ADD_RECEPTION')">
            <button type="button" class="btn btn-primary" :disabled="isLoader" @click="addCstmrColi">
              <i class="ph ph-plus-circle me-1"></i> Créer la réception
            </button>
          </footer>
        </section>
      </div>

      <div class="col-12 col-xl-6 d-flex flex-column gap-3">
        <!-- GLOBAL -->
        <section class="rc-card">
          <header class="rc-head">
            <span class="rc-icon rc-icon--blue"><i class="ph ph-globe-hemisphere-west"></i></span>
            <div>
              <h4>Réception globale</h4>
              <small>Tous les colis d'une agence</small>
            </div>
            <span class="rc-badge rc-badge--blue">GLOBAL</span>
          </header>
          <div class="rc-body">
            <label class="rc-label">Agence</label>
            <v-select class="custom-select-height" label="name" v-model="gobalAgency" :options="agencyList" placeholder="Choisir une agence"></v-select>
          </div>
          <footer class="rc-foot" v-if="auth.can('ADD_RECEPTION')">
            <button type="button" class="btn btn-primary" :disabled="isLoader" @click="addGlobalNote">
              <i class="ph ph-plus-circle me-1"></i> Créer la réception
            </button>
          </footer>
        </section>

        <!-- STOCK -->
        <section class="rc-card">
          <header class="rc-head">
            <span class="rc-icon rc-icon--green"><i class="ph ph-package"></i></span>
            <div>
              <h4>Réception stock</h4>
              <small>Colis de stock par agence</small>
            </div>
            <span class="rc-badge rc-badge--green">STOCK</span>
          </header>
          <div class="rc-body">
            <label class="rc-label">Agence</label>
            <v-select label="stock_agency_name" class="custom-select-height" v-model="stockAgency" :options="agencyStockList" placeholder="Choisir une agence">
              <template v-slot:option="option">
                <span>{{ option.stock_agency_name }} - {{ option.nb_colis }} Colis</span>
              </template>
            </v-select>
          </div>
          <footer class="rc-foot" v-if="auth.can('ADD_RECEPTION')">
            <button type="button" class="btn btn-primary" :disabled="isLoader" @click="addStockNote">
              <i class="ph ph-plus-circle me-1"></i> Créer la réception
            </button>
          </footer>
        </section>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { useAppToast } from "@/composables/useAppToast";
import router from "@/router";
import { useAuthStore } from "@/stores/auth";
import api from "@/utils/axios";
import { defineComponent, onMounted } from "vue";
    import { ref, reactive } from "vue";

export default defineComponent({
  setup() {


    const { showToast } = useAppToast();

    const auth = useAuthStore();
    const user = auth.user;

    // Use Vue's reactive state management for variables

    const agencyList = ref([]);
    const clientsList = ref([]);
    interface CstmrColi {
      customer?: string | number;
      magasin_id?: string | number;
      customer_name?: string;
      [key: string]: any;
    }
    const cstmrColi = ref<CstmrColi>({});
    const agencyStockList = ref([]);
    const codeColis = ref('');
    const isLoader = ref(false);
    const agency = ref<{ id?: number; name?: string; id_zone?: number; zone_name?: string }>({});
    const gobalAgency = ref<{ id?: number; name?: string; id_zone?: number; zone_name?: string }>({});
    const stockAgency = ref<{ stock_agency?: number; stock_agency_name?: string; id_zone?: number; zone_name?: string }>({});

    onMounted(async () => {
      try {
        const response = await api.get(`/api/auth/AgencyRamass`);

        agencyList.value = response.data;
      } catch (error) {
        console.error("Erreur lors du chargement des enregistrements:", error);
      }

      try {
        const response = await api.get(`/api/auth/ColiAgencyStock`);

        agencyStockList.value = response.data;
      } catch (error) {
        console.error("Erreur lors du chargement des enregistrements:", error);
      }
    });


    const onSearchAgencies = async (search: string, loading: (state: boolean) => void) => {
      if (search.length && agencyList.value.length === 0) {
        loading(true);
        try {
          const res = await api.get(`/api/auth/AgencyRamass`);
          agencyList.value = res.data;
        } finally {
          loading(false);
        }
      }
    };

    const onSearchAgencyStock = async (search: string, loading: (state: boolean) => void) => {
      if (search.length && agencyStockList.value.length === 0) {
        loading(true);
        try {
          const res = await api.get(`/api/auth/ColiAgencyStock`);
          agencyStockList.value = res.data;
        } finally {
          loading(false);
        }
      }
    };

    const onSearchClients = async (search: string, loading: (state: boolean) => void) => {
      if (search.length && clientsList.value.length === 0) {
        loading(true);
        try {
          const res = await api.get(`/api/auth/CstmrBL`);
          clientsList.value = res.data;
        } finally {
          loading(false);
        }
      }
    };
    const getCstmr = async () => {
      isLoader.value = true;
      try {
        const res = await api.get(`/api/auth/colis_cstmr`, { params: { code: codeColis.value } });
        isLoader.value = false;
        if (res.data.message == "success") {
          cstmrColi.value = res.data.cstmer;
          console.log(cstmrColi.value);
        } else if (res.data.message == 'not exist') {
          showToast('Colis n\'existe pas', "error");
        }
      } catch {
        showToast('Something went wrong! Pleaz try again', "error");
      }
    };

    const addCstmrColi = async () => {
      if (Object.keys(cstmrColi.value).length == 0 || Object.keys(agency.value).length == 0) {
        
        showToast('Merci de choisir un store et une agence', "error");
      } else {
        let data = new FormData();
        data.append('cstmr_id', String(cstmrColi.value.customer));
        data.append('magasin_id', String(cstmrColi.value.magasin_id));
        data.append('cstmr_name', cstmrColi.value.customer_name);
        data.append('agency_id', String(agency.value.id));
        data.append('agency_name', agency.value.name);
        data.append('id_zone', String(agency.value.id_zone));
        data.append('zone_name', agency.value.zone_name);
        data.append('user_id', user?.id);
        data.append('user_name', user?.name);
        data.append('type', 'CSTMR');
        isLoader.value = true;
        try {
          const res = await api.post(`/api/auth/Add_BRC`, data);
          isLoader.value = false;
          if (res.data.message == "success") {
            
            const routeData = router.resolve({ name: 'AddReceptionHCPage', params: { id: cstmrColi.value.customer, refNote: res.data.BrcRef.note_ref } });
            window.open(routeData.href, '_blank');
          }
        } catch {
          
          showToast('Something went wrong! Pleaz try again', "error");
        }
      }
    };

    const addGlobalNote = async () => {
      if (Object.keys(gobalAgency.value).length == 0) {
        showToast('Merci de choisir une agence', "error");
      } else {
        let data = new FormData();
        data.append('cstmr_id', '0');
        data.append('agency_id', String(gobalAgency.value.id));
        data.append('agency_name', gobalAgency.value.name);
        data.append('id_zone', String(gobalAgency.value.id_zone));
        data.append('zone_name', gobalAgency.value.zone_name);
        data.append('user_id', user?.id);
        data.append('user_name', user?.name);
        data.append('type', 'GLOBAL');
        isLoader.value = true;
        try {
          const res = await api.post(`/api/auth/Add_BRC`, data);
          isLoader.value = false;
          if (res.data.message == "success") {
            router.push({ name: 'AddReceptionGlobalPage', params: { id: res.data.BrcRef.note_customer, refNote: res.data.BrcRef.note_ref } });
          }
        } catch {
          
          showToast('Something went wrong! Pleaz try again', "error");
        }
      }
    };

    const addStockNote = async () => {
      if (Object.keys(stockAgency.value).length == 0) {
        
        showToast('Merci de choisir une agence', "error");
      } else {
        let data = new FormData();
        data.append('cstmr_id', '0');
        data.append('agency_id', String(stockAgency.value.stock_agency));
        data.append('agency_name', stockAgency.value.stock_agency_name);
        data.append('id_zone', String(stockAgency.value.id_zone));
        data.append('zone_name', stockAgency.value.zone_name);
        data.append('user_id', user?.id);
        data.append('user_name', user?.name);
        data.append('type', 'STOCK');
        isLoader.value = true;
        try {
          const res = await api.post(`/api/auth/Add_BRC`, data);
          isLoader.value = false;
          if (res.data.message == "success") {
            
            router.push({ name: 'AddReceptionStockPage', params: { id: res.data.BrcRef.note_customer, refNote: res.data.BrcRef.note_ref } });

          }
        } catch {
          
          showToast('Something went wrong! Pleaz try again', "error");
        }
      }
    }
      
    return {
      auth,
      agencyList,
      clientsList,
      cstmrColi,
      agencyStockList,
      codeColis,
      isLoader,
      agency,
      gobalAgency,
      stockAgency,
      getCstmr,
      addCstmrColi,
      addGlobalNote,
      addStockNote,
      onSearchClients,
      onSearchAgencyStock,
      onSearchAgencies
    };
      
  },
});
</script>

<style scoped>
.page-head { margin-bottom: 20px; }
.rc-card { background: #fff; border: 1px solid var(--brand-border, #e8e8ee); border-radius: 14px; display: flex; flex-direction: column; box-shadow: 0 2px 10px rgba(20, 20, 40, 0.04); transition: box-shadow 0.2s; }
.rc-card:hover { box-shadow: 0 6px 20px rgba(20, 20, 40, 0.08); }
.rc-head { display: flex; align-items: center; gap: 14px; padding: 18px 20px; border-bottom: 1px solid var(--brand-border, #eee); }
.rc-head h4 { font-size: 16px; font-weight: 700; margin: 0; }
.rc-head small { color: #8a8a99; }
.rc-icon { width: 44px; height: 44px; border-radius: 12px; display: grid; place-items: center; font-size: 22px; flex: none; background: rgba(var(--brand-accent-rgb, 255, 85, 0), 0.12); color: var(--brand-accent, #ff5500); }
.rc-icon--blue { background: rgba(37, 99, 235, 0.12); color: #2563eb; }
.rc-icon--green { background: rgba(22, 163, 74, 0.12); color: #16a34a; }
.rc-badge { margin-left: auto; font-size: 11px; font-weight: 700; letter-spacing: 0.06em; padding: 4px 10px; border-radius: 999px; background: rgba(var(--brand-accent-rgb, 255, 85, 0), 0.12); color: var(--brand-accent, #ff5500); }
.rc-badge--blue { background: rgba(37, 99, 235, 0.12); color: #2563eb; }
.rc-badge--green { background: rgba(22, 163, 74, 0.12); color: #16a34a; }
.rc-body { padding: 20px; flex: 1; }
.rc-label { display: block; font-size: 13px; font-weight: 600; margin-bottom: 6px; color: #555; }
.rc-inline { display: flex; gap: 10px; }
.rc-input-icon { position: relative; flex: 1; }
.rc-input-icon i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); font-size: 18px; color: #9a9aa8; }
.rc-input-icon .form-control { padding-left: 38px; height: 44px; }
.rc-btn { width: 52px; height: 44px; }
.rc-divider { display: flex; align-items: center; gap: 12px; margin: 20px 0; color: #9a9aa8; font-size: 12px; text-transform: uppercase; letter-spacing: 0.05em; }
.rc-divider::before, .rc-divider::after { content: ""; flex: 1; height: 1px; background: var(--brand-border, #e8e8ee); }
.rc-selected { margin-top: 16px; display: flex; align-items: center; gap: 8px; padding: 10px 14px; border-radius: 10px; background: rgba(22, 163, 74, 0.1); color: #15803d; font-size: 14px; }
.rc-selected i { font-size: 18px; }
.rc-foot { padding: 14px 20px; border-top: 1px solid var(--brand-border, #eee); display: flex; justify-content: flex-end; background: var(--brand-surface-sunken, #fafafc); border-radius: 0 0 14px 14px; }
.rc-foot .btn { min-width: 190px; }
</style>
